// @file The store for "Suggestions for you" on "Make" page (`DashDesktopAiMakeSuggestionsSection.vue`)
import { captureFetchException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { unboundedWatch } from '@@/bits/vue'
import * as PadletApi from '@@/dashboard/padlet_api'
import { useClassInformationStore } from '@@/pinia/class_information_store'
import { FetchError } from '@@/surface/api_fetch'
import { JsonApiError, JsonApiResponse, type AiMakeSuggestion } from '@@/types'
import { type JsonAPIResource } from '@padlet/arvo'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { SnackbarNotificationType, useGlobalSnackbarStore } from './global_snackbar'

const useDashAiMakeSuggestionsStore = defineStore('dashAiMakeSuggestionsStore', () => {
  const classInformationStore = useClassInformationStore()
  const snackbarStore = useGlobalSnackbarStore()

  const suggestions = ref<AiMakeSuggestion[] | undefined>(undefined)
  const inflightGenerationRequests = ref<number>(0)

  const isLoadingSuggestions = computed(() => inflightGenerationRequests.value > 0)
  const replacingSuggestionIds = ref<Set<string>>(new Set())

  async function fetchAiMakeSuggestions(): Promise<void> {
    inflightGenerationRequests.value++

    try {
      const response = await PadletApi.AiMakeSuggestionApi.fetchAiMakeSuggestions()
      suggestions.value = (response.data as JsonAPIResource<AiMakeSuggestion[]>).attributes
    } catch (error) {
      if (isConcurrentGenerationError(error)) {
        showConcurrentGenerationErrorSnackbar()
        return // don't update suggestions and loading state if it's a concurrent generation error
      }
      captureFetchException(error, { source: 'fetchAiMakeSuggestions' })
      suggestions.value = undefined
    } finally {
      inflightGenerationRequests.value--
    }
  }

  async function generateAiMakeSuggestions(): Promise<void> {
    inflightGenerationRequests.value++

    try {
      const response = await PadletApi.AiMakeSuggestionApi.generateAiMakeSuggestions()
      suggestions.value = (response.data as JsonAPIResource<AiMakeSuggestion[]>).attributes
    } catch (error) {
      if (isConcurrentGenerationError(error)) {
        showConcurrentGenerationErrorSnackbar()
        return // don't update suggestions and loading state if it's a concurrent generation error
      }
      captureFetchException(error, { source: 'generateAiMakeSuggestions' })
      suggestions.value = undefined
    } finally {
      inflightGenerationRequests.value--
    }
  }

  async function replaceAiMakeSuggestion(clickedId: string): Promise<void> {
    if (!clickedId) return
    replacingSuggestionIds.value.add(clickedId)
    try {
      const response = await PadletApi.AiMakeSuggestionApi.replaceAiMakeSuggestion(clickedId)
      suggestions.value = (response.data as JsonAPIResource<AiMakeSuggestion[]>).attributes
    } catch (error) {
      if (isConcurrentGenerationError(error)) {
        showConcurrentGenerationErrorSnackbar()
        return // don't log error if it's a concurrent generation error
      }
      captureFetchException(error, { source: 'replaceAiMakeSuggestion' })
      // On error the backend regenerates all three; if the call itself failed, leave suggestions as-is
    } finally {
      replacingSuggestionIds.value.delete(clickedId)
    }
  }

  function isConcurrentGenerationError(error: any): boolean {
    const fetchError = error as FetchError
    try {
      const response: JsonApiResponse<AiMakeSuggestion[]> = JSON.parse(fetchError.message)
      const jsonApiError: JsonApiError | undefined = response?.errors?.[0]
      return jsonApiError?.status === '409' && jsonApiError?.title === 'Concurrent Generation Error'
    } catch (e) {
      // catch possible parse error
      captureFetchException(e, { source: 'isConcurrentGenerationError' })
      return false
    }
  }

  function showConcurrentGenerationErrorSnackbar(): void {
    snackbarStore.setSnackbar({
      message: 'AI suggestions are currently being generated. Please try again in a few moments.',
      notificationType: SnackbarNotificationType.error,
    })
  }

  // Re-fetch suggestions when class information changes
  void unboundedWatch(
    () => classInformationStore.classInformation,
    () => {
      if (!isAppUsing('aiSuggestionsOnMakePage')) return
      void fetchAiMakeSuggestions()
    },
  )

  return {
    suggestions,
    isLoadingSuggestions,
    replacingSuggestionIds,
    fetchAiMakeSuggestions,
    generateAiMakeSuggestions,
    replaceAiMakeSuggestion,
  }
})
export { useDashAiMakeSuggestionsStore }
